import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RestaurantTableOperationDialogs from "./RestaurantTableOperationDialogs";
import { restaurantTablesApi } from "../../../api/tables";
import { tableReservationsApi } from "../../../api/tableReservations";

const UPCOMING_MINUTES = 60;
const ACTIVE_STATUSES = new Set(["pending", "confirmed"]);
const COLORS = {
  free: { bg: "#EAF7EF", border: "#2E7D32", label: "Libre" },
  reserved: { bg: "#FDECEC", border: "#C62828", label: "Reservada" },
  upcoming: { bg: "#FFF4E5", border: "#ED6C02", label: "Próxima" },
  occupied: { bg: "#EEF2FF", border: "#455A64", label: "Ocupada" },
  out: { bg: "#F2F3F5", border: "#7B8794", label: "Fuera de servicio" },
};

function localInput(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function tableIds(reservation) {
  const ids = Array.isArray(reservation?.tableIds) ? [...reservation.tableIds] : [];
  if (!ids.length && reservation?.tableId) ids.push(reservation.tableId);
  return Array.from(new Set(ids.filter(Boolean).map(String)));
}

function dateTime(value, timezone) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: timezone || undefined,
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function shapeRadius(shape) {
  if (shape === "round") return "50%";
  return shape === "square" ? 14 : 18;
}

function statusFor(table, current, upcoming) {
  if (table.operationalStatus === "out_of_service") return "out";
  if (current.length) return "reserved";
  if (table.operationalStatus === "occupied") return "occupied";
  if (upcoming.length) return "upcoming";
  if (table.operationalStatus === "reserved") return "reserved";
  return "free";
}

function ReservationActions({ reservation, onReservation, onCustomer }) {
  return (
    <Stack direction="row" spacing={.75} sx={{ mt: .9 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={() => onReservation(reservation)}
        sx={{ minHeight: 40, flex: 1, textTransform: "none", fontWeight: 850 }}
      >
        Reserva
      </Button>
      <Button
        size="small"
        onClick={() => onCustomer(reservation)}
        sx={{ minHeight: 40, flex: 1, textTransform: "none", fontWeight: 850 }}
      >
        Cliente
      </Button>
    </Stack>
  );
}

export default function RestaurantTableOperationsView({ projectId }) {
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const [layout, setLayout] = React.useState(null);
  const [sector, setSector] = React.useState("");
  const [selectedAt, setSelectedAt] = React.useState(() => localInput());
  const [reservations, setReservations] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState("");
  const [reservationDialog, setReservationDialog] = React.useState(null);
  const [customerDialog, setCustomerDialog] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    const point = new Date(selectedAt);
    if (!Number.isFinite(point.getTime())) return;
    try {
      setLoading(true);
      setError("");
      const from = new Date(point.getTime() - 24 * 60 * 60_000).toISOString();
      const to = new Date(point.getTime() + UPCOMING_MINUTES * 60_000).toISOString();
      const [nextLayout, rows] = await Promise.all([
        restaurantTablesApi.getLayout(projectId, sector),
        tableReservationsApi.list(projectId, { from, to }),
      ]);
      setLayout(nextLayout);
      setSector(nextLayout?.sector || "");
      setReservations(rows.filter((row) => ACTIVE_STATUSES.has(row.status)));
      setSelectedId((current) => nextLayout?.tables?.some((table) => table.id === current) ? current : "");
    } catch (err) {
      setError(err.message || "No se pudo cargar la operación de mesas.");
    } finally {
      setLoading(false);
    }
  }, [projectId, sector, selectedAt]);

  React.useEffect(() => {
    const timer = window.setTimeout(load, 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  const pointMs = new Date(selectedAt).getTime();
  const selectedAtValid = Number.isFinite(pointMs);
  const horizonMs = selectedAtValid ? pointMs + UPCOMING_MINUTES * 60_000 : Number.NaN;
  const byTable = React.useMemo(() => {
    const map = new Map();
    if (!selectedAtValid) return map;
    reservations.forEach((reservation) => {
      const start = new Date(reservation.startAt).getTime();
      const end = new Date(reservation.endAt || (start + Number(reservation.durationMinutes || 90) * 60_000)).getTime();
      const kind = start <= pointMs && end > pointMs ? "current" : start > pointMs && start <= horizonMs ? "upcoming" : "";
      if (!kind) return;
      tableIds(reservation).forEach((id) => {
        if (!map.has(id)) map.set(id, { current: [], upcoming: [] });
        map.get(id)[kind].push(reservation);
      });
    });
    map.forEach((entry) => entry.upcoming.sort((a, b) => new Date(a.startAt) - new Date(b.startAt)));
    return map;
  }, [reservations, pointMs, horizonMs, selectedAtValid]);

  const tableStates = React.useMemo(() => {
    if (!selectedAtValid) return [];
    return (layout?.tables || []).map((table) => {
      const timeline = byTable.get(String(table.id)) || { current: [], upcoming: [] };
      return { table, timeline, status: statusFor(table, timeline.current, timeline.upcoming) };
    });
  }, [layout, byTable, selectedAtValid]);

  const counts = tableStates.reduce((acc, item) => ({ ...acc, [item.status]: (acc[item.status] || 0) + 1 }), {});
  const selectedState = tableStates.find((item) => item.table.id === selectedId) || null;
  const selectedReservations = selectedState ? [...selectedState.timeline.current, ...selectedState.timeline.upcoming] : [];

  if (selectedAtValid && loading && !layout) return <Box sx={{ minHeight: 360, display: "grid", placeItems: "center" }}><CircularProgress /></Box>;

  return (
    <Stack spacing={{ xs: 1.75, md: 2.2 }} sx={{ minWidth: 0 }}>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.2} justifyContent="space-between" alignItems={{ lg: "center" }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Operación de mesas</Typography>
          <Typography variant="body2" color="text.secondary">Elegí horario y tocá una mesa para operar el salón.</Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "minmax(150px,.8fr) minmax(230px,1.2fr) auto auto" }, gap: 1, width: { xs: "100%", lg: "auto" } }}>
          <TextField select size="small" label="Sector" value={sector} onChange={(event) => setSector(event.target.value)} sx={{ minWidth: 0 }}>
            {(layout?.sectors || [""]).map((item) => <MenuItem key={item || "__default__"} value={item}>{item || "Sin sector"}</MenuItem>)}
          </TextField>
          <TextField size="small" type="datetime-local" label="Fecha y hora" value={selectedAt} onChange={(event) => setSelectedAt(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }} />
          <Button variant="outlined" startIcon={<AccessTimeRoundedIcon />} onClick={() => setSelectedAt(localInput())} sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }}>Ahora</Button>
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading || !selectedAtValid} sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }}>Actualizar</Button>
        </Box>
      </Stack>

      {!selectedAtValid ? (
        <Alert severity="info" sx={{ width: "100%" }}>Elegí una fecha y hora para ver el estado real de las mesas.</Alert>
      ) : error ? <Alert severity="error">{error}</Alert> : null}

      {selectedAtValid ? (<>
      <Box sx={{ overflowX: "auto", mx: { xs: -1, sm: 0 }, px: { xs: 1, sm: 0 }, WebkitOverflowScrolling: "touch" }}>
        <Stack direction="row" spacing={1} sx={{ width: "max-content", minWidth: "100%" }}>
          {["free", "reserved", "upcoming", "occupied", "out"].map((key) => (
            <Chip key={key} label={`${COLORS[key].label}: ${counts[key] || 0}`} sx={{ bgcolor: COLORS[key].bg, border: `1px solid ${COLORS[key].border}`, fontWeight: 800 }} />
          ))}
        </Stack>
      </Box>

      {mobile ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1 }}>
          {tableStates.map(({ table, status, timeline }) => {
            const style = COLORS[status];
            const nextReservation = timeline.current[0] || timeline.upcoming[0];
            const selected = selectedId === table.id;
            return (
              <Paper
                key={table.id}
                component="button"
                type="button"
                variant="outlined"
                onClick={() => setSelectedId(table.id)}
                sx={{
                  p: 1.25,
                  minHeight: 112,
                  borderRadius: 2.5,
                  border: `${selected ? 3 : 1}px solid ${style.border}`,
                  bgcolor: style.bg,
                  color: "inherit",
                  textAlign: "left",
                  font: "inherit",
                  cursor: "pointer",
                  boxShadow: selected ? "0 8px 20px rgba(25,36,47,.14)" : "none",
                }}
              >
                <Stack spacing={.45}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={.5}>
                    <Typography sx={{ fontWeight: 950, fontSize: 17 }}>Mesa {table.identifier}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>{COLORS[status].label}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: .5 }}><PeopleAltRoundedIcon sx={{ fontSize: 16 }} />{table.capacity} lugares</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                    {nextReservation ? `${nextReservation.customer?.fullName || "Reserva"} · ${dateTime(nextReservation.startAt, nextReservation.timezone)}` : table.name || table.sector || "Sin reservas próximas"}
                  </Typography>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 1, borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{ position: "relative", width: "100%", aspectRatio: `${layout?.canvas?.width || 1200} / ${layout?.canvas?.height || 760}`, minHeight: { sm: 420, md: 460 }, bgcolor: "#FAFBFC", borderRadius: 2, overflow: "hidden", backgroundImage: "linear-gradient(rgba(25,36,47,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(25,36,47,.05) 1px, transparent 1px)", backgroundSize: "20px 20px" }}>
            {tableStates.map(({ table, status }) => {
              const item = table.layout || {};
              const style = COLORS[status];
              return (
                <Box key={table.id} role="button" tabIndex={0} onClick={() => setSelectedId(table.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setSelectedId(table.id); }} sx={{ position: "absolute", left: `${(Number(item.x || 0) / layout.canvas.width) * 100}%`, top: `${(Number(item.y || 0) / layout.canvas.height) * 100}%`, width: `${(Number(item.width || 96) / layout.canvas.width) * 100}%`, height: `${(Number(item.height || 96) / layout.canvas.height) * 100}%`, minWidth: 46, minHeight: 46, borderRadius: shapeRadius(item.shape), bgcolor: style.bg, border: `${selectedId === table.id ? 4 : 2}px solid ${style.border}`, boxShadow: selectedId === table.id ? "0 8px 20px rgba(25,36,47,.18)" : "0 3px 10px rgba(25,36,47,.08)", display: "grid", placeItems: "center", textAlign: "center", cursor: "pointer", userSelect: "none", px: .5 }}>
                  <Box sx={{ pointerEvents: "none" }}><Typography sx={{ fontWeight: 950, fontSize: { sm: 13, md: 14 } }}>{table.identifier}</Typography><Typography variant="caption" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: .3 }}><PeopleAltRoundedIcon sx={{ fontSize: 13 }} />{table.capacity}</Typography></Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 950 }}>Mesa seleccionada</Typography>
          {selectedState ? <Chip size="small" label={COLORS[selectedState.status].label} sx={{ bgcolor: COLORS[selectedState.status].bg, border: `1px solid ${COLORS[selectedState.status].border}`, fontWeight: 850 }} /> : null}
        </Stack>
        <Divider sx={{ my: 1.3 }} />
        {!selectedState ? <Typography variant="body2" color="text.secondary">Tocá una mesa para ver reservas y datos útiles.</Typography> : (
          <Stack spacing={1.2}>
            <Box><Typography variant="h6" sx={{ fontWeight: 950 }}>Mesa {selectedState.table.identifier}</Typography><Typography variant="body2" color="text.secondary">{selectedState.table.name || selectedState.table.sector || `${selectedState.table.capacity} lugares`}</Typography></Box>
            {!selectedReservations.length ? <Typography variant="body2" color="text.secondary">Sin reservas activas ni próximas en los siguientes {UPCOMING_MINUTES} minutos.</Typography> : selectedReservations.map((reservation) => (
              <Paper key={reservation.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>{reservation.customer?.fullName || "Cliente"} · {reservation.partySize} personas</Typography>
                <Typography variant="caption" color="text.secondary">{dateTime(reservation.startAt, reservation.timezone)}</Typography>
                <ReservationActions reservation={reservation} onReservation={setReservationDialog} onCustomer={setCustomerDialog} />
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
      </>) : null}

      <RestaurantTableOperationDialogs
        reservation={reservationDialog}
        customerReservation={customerDialog}
        onCloseReservation={() => setReservationDialog(null)}
        onCloseCustomer={() => setCustomerDialog(null)}
      />
    </Stack>
  );
}
